<script setup>
import { computed } from 'vue'

/**
 * HalftoneVeil — voile de raccord en demi-teinte (trame de points).
 *
 * Remplace les fondus dégradés posés sur les images : au lieu d'un voile
 * laiteux, une trame de petits points de la couleur de la section voisine
 * densifie vers le bord de rencontre (deux couches de points décalées +
 * un fin liseré plein au ras du bord pour une couture nette). L'image reste
 * lisible sous la trame — l'effet est celui d'une demi-teinte d'imprimerie,
 * pas d'un dégradé.
 *
 * `color` : couleur de la section que le visuel rejoint (hex).
 * `side`  : bord de rencontre ('bottom' par défaut, 'top' pour l'inverse).
 * Posé en `absolute` par le parent (inset-x-0 + hauteur, ou inset-0).
 */
const props = defineProps({
  color: { type: String, default: '#ffffff' },
  side: {
    type: String,
    default: 'bottom',
    validator: (value) => ['top', 'bottom'].includes(value)
  }
})

const to = computed(() => (props.side === 'bottom' ? 'to bottom' : 'to top'))

// Les points sont posés en translucide (color-mix) : la trame texture
// l'image sans jamais poser d'aplat — seul le liseré de couture est plein.
// Les gros points, plus voyants, sont les plus transparents et arrivent
// le plus tard.

// Couche 1 — points fins, apparition précoce
const fineLayer = computed(() => ({
  backgroundImage: `radial-gradient(circle, color-mix(in srgb, ${props.color} 55%, transparent) 1px, transparent 1.1px)`,
  backgroundSize: '9px 9px',
  maskImage: `linear-gradient(${to.value}, transparent 30%, black 80%)`,
  WebkitMaskImage: `linear-gradient(${to.value}, transparent 30%, black 80%)`
}))

// Couche 2 — points larges décalés d'une demi-maille, plus tardifs :
// la trame « grossit » vers le bord, comme une vraie demi-teinte
const boldLayer = computed(() => ({
  backgroundImage: `radial-gradient(circle, color-mix(in srgb, ${props.color} 42%, transparent) 2px, transparent 2.2px)`,
  backgroundSize: '18px 18px',
  backgroundPosition: '9px 9px',
  maskImage: `linear-gradient(${to.value}, transparent 62%, black 96%)`,
  WebkitMaskImage: `linear-gradient(${to.value}, transparent 62%, black 96%)`
}))

// Liseré plein au ras du bord : la couture avec la section est nette
const seam = computed(() => ({
  background: props.color,
  [props.side]: '0'
}))
</script>

<template>
  <div class="pointer-events-none" aria-hidden="true">
    <div class="absolute inset-0" :style="fineLayer"></div>
    <div class="absolute inset-0" :style="boldLayer"></div>
    <div class="absolute inset-x-0 h-[14px]" :style="seam"></div>
  </div>
</template>
